import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import GeneralAdminLink from '~/components/app/GeneralAdminLink';
import LogoutMenuItem from '~/components/app/LogoutMenuItem';
import PlaceholderLink from '~/components/app/PlaceholderLink';
import WebsiteSettingsLink from '~/components/app/WebsiteSettingsLink';
import YourProfileLink from '~/components/app/YourProfileLink';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import ExpandableNavSection from '~/components/patterns/navigations/ExpandableNavSection';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';

import {
	type messages
} from '../MainNavigation';



type Props = {
	accountId: CK.AccountId | null,
	collapseAllExpandables?: () => void,
	collapseExpandablesExceptAccount?: () => void,
	effectiveHomeAccountId: CK.AccountId | null,
	isAccountExpanded?: boolean,
	isAnyExpandableExpanded?: boolean,
	isInternalExpanded?: boolean,
	kingdomAdminFeatures?: {
		areAlwaysVisible?: boolean,
		areVisible?: boolean,
	},
	messages: typeof messages,
	onAccountToggle?: () => void,
	onInternalToggle?: () => void,
	openSwitchAccountModal?: () => void,
	showIntegrations?: boolean,
	websiteId: CK.WebsiteId | null,
};

/**
 * Build secondary navigation items (Integrations, Settings, Account)
 *
 * Returns an array of JSX elements for the secondary navigation section
 */
export function buildNewSecondaryItems(props: Props): Array<React.ReactElement> {
	const {
		accountId,
		collapseAllExpandables,
		collapseExpandablesExceptAccount,
		effectiveHomeAccountId,
		isAccountExpanded = false,
		isAnyExpandableExpanded = false,
		isInternalExpanded = false,
		kingdomAdminFeatures,
		messages,
		onAccountToggle,
		onInternalToggle,
		openSwitchAccountModal,
		showIntegrations = false,
		websiteId,
	} = props;

	// Use areAlwaysVisible so Internal shows for kingdom admins regardless of Toggle Admin state
	const showInternal = kingdomAdminFeatures?.areAlwaysVisible === true;

	const items: Array<React.ReactElement> = [
		// Integrations (SSO only)
		...(showIntegrations ? [
			<li
				className="main-nav__item"
				data-secondary-nav-item="true"
				key="integrations-item"
			>
				<PlaceholderLink
					onClick={collapseAllExpandables}
					sectionName="integrations"
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon
										size={20}
										type={NavigationIconType.Integrations}
									/>
								)}
								isActive={isActive && !isAnyExpandableExpanded}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.integrations} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>,
		] : []),
		// Settings
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="settings-item"
		>
			<WebsiteSettingsLink
				onClick={collapseAllExpandables}
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Settings}
							/>
						)}
						isActive={isActive && !isAnyExpandableExpanded}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.settings} />
						)}
					/>
				)}
			</WebsiteSettingsLink>
		</li>,
		// Account (collapsible): EXACT same structure as Technical - button, no isActive, no Link
		<ExpandableNavSection
			dataAttributes={{ 'data-secondary-nav-item': 'true' }}
			icon={(
				<NavigationIcon
					size={20}
					type={NavigationIconType.Account}
				/>
			)}
			isExpanded={isAccountExpanded}
			isSubItemsHidden={false}
			key="account-section"
			label={(
				<FormattedMessage {...messages.account} />
			)}
			onToggle={onAccountToggle ?? (() => {})}
		>
			{/* Manage My Profile - keep Account expanded when navigating */}
			<li
				className="main-nav__item"
				key="account-manage-profile-item"
			>
				<YourProfileLink
					accountId={accountId ?? effectiveHomeAccountId ?? undefined}
					onClick={collapseExpandablesExceptAccount ?? collapseAllExpandables}
				>
					{({ isActive, isDimmed }) => (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.manageMyProfile} />
							)}
						/>
					)}
				</YourProfileLink>
			</li>
			{/* Log out */}
			<li
				className="main-nav__item"
				key="account-logout-item"
			>
				<LogoutMenuItem
					isIconHidden={true}
					isLabelHidden={false}
				/>
			</li>
		</ExpandableNavSection>,
	];

	// Internal (collapsible) - only shown for kingdom admins when admin features are visible
	// EXACT same structure as Technical - button, no isActive, no Link
	if (showInternal && onInternalToggle && openSwitchAccountModal) {
		items.push(
			<ExpandableNavSection
				dataAttributes={{ 'data-secondary-nav-item': 'true' }}
				icon={(
					<NavigationIcon
						size={20}
						type={NavigationIconType.LockInSquare}
					/>
				)}
				isExpanded={isInternalExpanded}
				isSubItemsHidden={false}
				key="internal-section"
				label={(
					<FormattedMessage {...messages.internal} />
				)}
				onToggle={onInternalToggle}
			>
				{/* Switch Account */}
				<li
					className="main-nav__item"
					key="internal-switch-account-item"
				>
					<MenuItemLayout
						icon={<></>}
						isActive={false}
						isDimmed={false}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.switchAccount} />
						)}
						onClickCallback={() => openSwitchAccountModal()}
					/>
				</li>
				{/* General Admin */}
				<li
					className="main-nav__item"
					key="internal-general-admin-item"
				>
					<GeneralAdminLink>
						{({ isActive, isDimmed }) => (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.generalAdmin} />
								)}
							/>
						)}
					</GeneralAdminLink>
				</li>
			</ExpandableNavSection>,
		);
	}

	return items;
}
